import { type CoordinateFormat, parseCoordinateValue } from '@gmn-portal/backend';
import { formatStoredCoordinatePart } from './coordinates';
import { buildCsv, type CsvDialect, formatCsvNumber } from './csv';

/** Structural subset of a position entry that is enough to export it. */
export type PositionsCsvEntry = {
  label?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  status?: boolean | null;
};

export type PositionsCsvLabels = {
  label: string;
  latitude: string;
  longitude: string;
  latitudeDecimal: string;
  longitudeDecimal: string;
  /** Header of the per-position status column; omit when the field has no status. */
  status?: string;
  yes: string;
  no: string;
};

export type PositionsCsvOptions = {
  coordinateFormat: CoordinateFormat;
  labels: PositionsCsvLabels;
  dialect: CsvDialect;
};

/**
 * Builds the CSV matrix for a positions field: the coordinates both in the
 * format the user sees in the portal and as raw decimal degrees, so the export
 * is usable in spreadsheets as well as in GIS tools.
 */
export const buildPositionsCsvRows = (
  positions: PositionsCsvEntry[],
  { coordinateFormat, labels, dialect }: PositionsCsvOptions
): string[][] => {
  const withStatus = Boolean(labels.status);

  const header = [
    labels.label,
    labels.latitude,
    labels.longitude,
    labels.latitudeDecimal,
    labels.longitudeDecimal,
    ...(withStatus ? [labels.status as string] : []),
  ];

  const rows = positions.map((position) => [
    position.label ?? '',
    formatStoredCoordinatePart(position.latitude ?? undefined, 'latitude', coordinateFormat),
    formatStoredCoordinatePart(position.longitude ?? undefined, 'longitude', coordinateFormat),
    formatCsvNumber(parseCoordinateValue(position.latitude, 'latitude'), dialect, 6),
    formatCsvNumber(parseCoordinateValue(position.longitude, 'longitude'), dialect, 6),
    ...(withStatus ? [position.status ? labels.yes : labels.no] : []),
  ]);

  return [header, ...rows];
};

export const buildPositionsCsv = (
  positions: PositionsCsvEntry[],
  options: PositionsCsvOptions
): string => {
  return buildCsv(buildPositionsCsvRows(positions, options), options.dialect);
};
